import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Image as ImageIcon, Trash2, Upload } from "lucide-react";
import { useBranding, logoSrc } from "@/context/BrandingContext";

const SLOTS = [
  ["sidebar", "Logo hell (Seitenleiste)", "Wird oben links in der dunklen Seitenleiste angezeigt.", "brand-sidebar"],
  ["pdf", "Logo für PDF-Dokumente", "Erscheint im Kopf der PDF-Exporte (z. B. Mail-Protokoll).", "bg-white"],
  ["invoice", "Logo für Rechnungen", "Für spätere Rechnungen an Anzeigenkunden.", "bg-white"],
  ["favicon", "App-Symbol (Browser-Tab & Lesezeichen)", "Optional, quadratisch (am besten SVG). Leer = automatisch das Vereinswappen aus dem PDF-Logo.", "bg-white"],
];

function LogoSlot({ slot, label, hint, previewBg, source, src, canRemove = true, onSaved }) {
  const [url, setUrl] = useState(source?.startsWith("Datei:") ? "" : source || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setUrl(source?.startsWith("Datei:") ? "" : source || ""); }, [source]);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("slot", slot); fd.append("file", file);
    setBusy(true);
    try { await api.post("/branding/logos/upload", fd); toast.success("Logo hochgeladen"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const save = async (value) => {
    setBusy(true);
    try { await api.post("/branding/logos", { slot, url: value }); toast.success(value ? "Logo übernommen" : "Logo entfernt"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-slate-200 rounded-lg p-4" data-testid={`logo-slot-${slot}`}>
      <div className="flex items-start gap-4">
        <div className={`h-20 w-32 shrink-0 rounded-md border border-slate-200 grid place-items-center overflow-hidden ${previewBg}`}>
          {src ? <img src={src} alt={label} className="max-h-[72px] max-w-[120px] object-contain" data-testid={`logo-preview-${slot}`} /> : <ImageIcon size={22} className="text-slate-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <Label>{label}</Label>
          <p className="text-xs text-slate-400 mb-2">{hint}</p>
          <div className="flex gap-2">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… (Bild- oder Freigabe-Link)" data-testid={`logo-url-${slot}`} />
            <Button onClick={() => save(url)} disabled={busy || !url.trim()} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid={`logo-save-${slot}`}>Übernehmen</Button>
            {src && canRemove && <Button variant="outline" onClick={() => save("")} disabled={busy} title="Logo entfernen" data-testid={`logo-remove-${slot}`}><Trash2 size={15} /></Button>}
          </div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <label className={`inline-flex items-center gap-1.5 text-sm font-medium border border-slate-200 rounded-md px-3 py-1.5 cursor-pointer hover:bg-slate-50 ${busy ? "opacity-50 pointer-events-none" : ""}`} data-testid={`logo-upload-${slot}`}>
              <Upload size={14} /> Datei hochladen
              <input type="file" className="hidden" accept={slot === "sidebar" || slot === "favicon" ? ".png,.jpg,.jpeg,.webp,.svg" : ".png,.jpg,.jpeg,.webp"}
                onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`logo-file-${slot}`} />
            </label>
            <span className="text-xs text-slate-400">{slot === "sidebar" ? "PNG, JPG, WebP oder SVG (am schärfsten)" : slot === "favicon" ? "SVG (empfohlen, quadratisch) oder PNG mind. 512 px" : "PNG oder JPG, am besten mind. 1000 px breit"} · max. 5 MB</span>
            {source?.startsWith("Datei:") && <span className="text-xs text-slate-500" data-testid={`logo-source-${slot}`}>Aktuell: {source.slice(7)}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GeneralSettings() {
  const [form, setForm] = useState(null);
  const { branding, refresh } = useBranding();

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await api.put("/settings", {
        magazine_name: form.magazine_name,
        vat_rate: Number(String(form.vat_rate ?? 19).replace(",", ".")),
        payment_terms: form.payment_terms,
        reply_to: form.reply_to,
        app_name: form.app_name,
        app_subtitle: form.app_subtitle,
        login_headline: form.login_headline,
        login_text: form.login_text,
      });
      toast.success("Einstellungen gespeichert");
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!form) return <div className="text-slate-400">Lädt…</div>;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>App-Name (Seitenleiste oben)</Label>
            <Input value={form.app_name ?? ""} onChange={(e) => setForm({ ...form, app_name: e.target.value })} className="mt-1" data-testid="settings-app-name" />
          </div>
          <div>
            <Label>Untertitel</Label>
            <Input value={form.app_subtitle ?? ""} onChange={(e) => setForm({ ...form, app_subtitle: e.target.value })} className="mt-1" data-testid="settings-app-subtitle" />
          </div>
        </div>
        <div>
          <Label>Mehrwertsteuersatz für Anzeigen (%)</Label>
          <Input value={form.vat_rate ?? 19} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} inputMode="decimal" className="mt-1 w-32" data-testid="settings-vat-rate" />
          <p className="text-xs text-slate-500 mt-1">Alle Anzeigenpreise sind netto. Der Satz wird auf Buchungsseite, in Mails, im Zusage-Fenster und in den Auswertungen ausgewiesen.</p>
        </div>
        <div>
          <Label>Zahlungsbedingungen für Anzeigen</Label>
          <Input value={form.payment_terms ?? ""} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="mt-1" data-testid="settings-payment-terms" />
          <p className="text-xs text-slate-500 mt-1">Erscheint in der Zusage-Mail zusammen mit der Auftragsnummer (Platzhalter {"{{zahlungsbedingungen}}"}).</p>
        </div>
        <div>
          <Label>Magazin-Name (Absender-Anzeigename)</Label>
          <Input value={form.magazine_name || ""} onChange={(e) => setForm({ ...form, magazine_name: e.target.value })} className="mt-1" data-testid="settings-magazine-name" />
        </div>
        <div>
          <Label>Antwort-Adresse (Reply-To)</Label>
          <Input type="email" value={form.reply_to || ""} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} className="mt-1" data-testid="settings-reply-to" />
          <p className="text-xs text-slate-400 mt-1">Antworten der Kunden gehen an diese Adresse. Der Versand erfolgt über Dein Microsoft-365-Postfach (Einstellungen → E-Mail-Versand).</p>
        </div>
        <div className="border-t border-slate-100 pt-5 space-y-4" data-testid="login-texts-section">
          <div className="text-sm font-semibold text-slate-800">Anmeldeseite (rechte Seite)</div>
          <div>
            <Label>Überschrift</Label>
            <Input value={form.login_headline ?? ""} onChange={(e) => setForm({ ...form, login_headline: e.target.value })} className="mt-1" placeholder="Redaktionssystem" data-testid="settings-login-headline" />
          </div>
          <div>
            <Label>Text</Label>
            <Textarea value={form.login_text ?? ""} onChange={(e) => setForm({ ...form, login_text: e.target.value })} className="mt-1" rows={3} data-testid="settings-login-text" />
          </div>
        </div>
        <p className="text-xs text-slate-400">Farben und Schriften verwaltest Du unter Einstellungen → Farbprofile & Schriften.</p>
        <div className="pt-2">
          <Button onClick={save} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="settings-save-button">
            <Save size={16} className="mr-1.5" /> Speichern
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-800">Logos</h2>
          <p className="text-sm text-slate-500">Lade eine Datei hoch oder übernimm einen Link – das Bild wird in der App gespeichert. Freigabe-Links (z. B. BrandBay) werden automatisch aufgelöst, liefern aber oft nur eine kleine Vorschau; für scharfe Darstellung besser die Originaldatei hochladen.</p>
        </div>
        {SLOTS.map(([slot, label, hint, bg]) => (
          <LogoSlot key={slot} slot={slot} label={label} hint={hint} previewBg={bg}
            source={branding?.logo_sources?.[slot]} src={slot === "favicon" ? logoSrc(branding?.logos?.favicon || `/api/branding/favicon.png?size=128&v=${(branding?.logos?.pdf || "").replace(/\D/g, "")}`) : logoSrc(branding?.logos?.[slot])} canRemove={slot !== "favicon" || !!branding?.logos?.favicon} onSaved={refresh} />
        ))}
      </div>
    </div>
  );
}
